// Regras puras da trilha de auditoria e dos administradores (WEB-84). Sem React: o teste
// roda sem provider e sem DOM.

import { isApiError } from '../../api/errors.ts';
import { addDaysToIsoDate, formatShortDate, formatTime, toIsoDate } from '../../lib/dates.ts';
import type { AuditAction, AuditActor, AuditEntry, AuditTarget } from './queries.ts';

/**
 * Opções do filtro de ação. Os VALORES são escritos aqui, mas o TIPO vem do enum gerado
 * (`AuditAction`, de `types/admin.d.ts`): um nome que saia do catálogo da API quebra o
 * typecheck em vez de virar `400 validation_failed` na cara do operador. Copiada do enum
 * em `admin.d.ts`, não da lista antiga de `admin-system/model.ts` (que perdeu cinco
 * nomes desde AUTH-06/INT-01).
 */
export const AUDIT_ACTIONS: readonly AuditAction[] = [
  'acquirer.create',
  'acquirer.test',
  'acquirer.update',
  'admin.create',
  'admin.remove',
  'alert.ack',
  'api_key.create',
  'api_key.revoke',
  'api_key.rotate',
  'invite.create',
  'invite.revoke',
  'kyc.approve',
  'kyc.purge_files',
  'kyc.reject',
  'kyc.view_file',
  'ledger.adjustment',
  'pix_key.create',
  'pix_key.deactivate',
  'pix_key.verify',
  'postback_secret.rotate',
  'rate_settings.reset_seller',
  'rate_settings.update_default',
  'rate_settings.update_seller',
  'reconciliation.run',
  'report.export',
  'seller.acquirer_set',
  'seller.close',
  'seller.flags',
  'seller.lock_payout',
  'seller.reactivate',
  'seller.suspend',
  'seller.unlock_payout',
  'settings.default_acquirer',
  'settings.payouts_paused',
  'system.run_job',
  'transaction.refund',
  'transaction.release_reserve',
  'user.2fa_disable',
  'user.2fa_enable',
  'user.2fa_recover',
  'user.email_change',
  'user.password_change',
  'user.step_up',
  'user.step_up_prefs',
  'user.terms_accept',
  'webhook_endpoint.create',
  'webhook_endpoint.delete',
  'webhook_endpoint.rotate_secret',
  'webhook_endpoint.update',
  'webhook_inbox.reprocess',
  'withdrawal.approve',
  'withdrawal.cancel',
  'withdrawal.mark_failed',
  'withdrawal.poll',
  'withdrawal.record_partner_cost',
  'withdrawal.reject',
  'withdrawal.request',
];

export function isAuditAction(value: string): value is AuditAction {
  return AUDIT_ACTIONS.some((candidate) => candidate === value);
}

/** Recorte de período do croqui (`<Select value="Últimos 7 dias" />`). */
export const PERIODS = [
  { id: '7d', label: 'Últimos 7 dias', days: 7 },
  { id: '30d', label: 'Últimos 30 dias', days: 30 },
  { id: '90d', label: 'Últimos 90 dias', days: 90 },
  { id: 'all', label: 'Todo o período', days: 0 },
] as const;

export const DEFAULT_PERIOD = '7d';

export function isPeriod(value: string): boolean {
  return PERIODS.some((period) => period.id === value);
}

export function periodLabel(id: string): string {
  return PERIODS.find((period) => period.id === id)?.label ?? '';
}

/**
 * `from` da query (`z.iso.datetime()` na rota, `spec/12` §4.3), não `YYYY-MM-DD`.
 * `all` devolve `undefined`: sem `from` a API não recorta nada.
 */
export function periodFrom(id: string, now: Date = new Date()): string | undefined {
  const days = PERIODS.find((period) => period.id === id)?.days ?? 0;
  if (days === 0) {
    return undefined;
  }
  return new Date(now.getTime() - days * 86_400_000).toISOString();
}

/**
 * A busca do croqui é UMA caixa ("Filtrar por seller_id ou id do alvo") e a API tem DOIS
 * parâmetros. O prefixo decide: `sel_…` é seller, o resto é o id do alvo. Registrado em
 * "Questões abertas".
 */
export function targetFilterOf(q: string | undefined): { seller_id?: string; target_id?: string } {
  const trimmed = q?.trim() ?? '';
  if (trimmed === '') {
    return {};
  }
  return trimmed.startsWith('sel_') ? { seller_id: trimmed } : { target_id: trimmed };
}

const ACTOR_LABEL: Record<AuditActor['type'], string> = {
  admin: 'Admin',
  seller: 'Seller',
  system: 'Sistema',
};

/** Linha de cima da célula "Admin": o email quando a API o resolve, senão o papel. */
export function actorName(actor: AuditActor): string {
  return actor.email ?? ACTOR_LABEL[actor.type];
}

/**
 * `· Seller` / `· Sistema` ao lado do nome. A coluna do croqui se chama "Admin" e a
 * trilha também guarda ação de seller e do próprio sistema: sem esta marca a linha
 * mentiria sobre quem agiu.
 */
export function actorRole(actor: AuditActor): string | undefined {
  return actor.type === 'admin' ? undefined : ACTOR_LABEL[actor.type];
}

/** `seller · sel_… · Loja do Rafa`, `label` só existe quando a API o resolveu (API-28). */
export function targetLabel(target: AuditTarget): string | undefined {
  return target.label;
}

/**
 * Coluna "Último acesso" (`last_login_at`, API-28), no vocabulário do croqui:
 * `nunca` · `agora` · `18:20` (hoje) · `ontem 18:20` · `04/05`. O admin que faz
 * sign-out perde a linha de sessão e volta a "nunca" (API-28, item 6).
 */
export function formatLastLogin(value: string | null, now: Date = new Date()): string {
  if (value === null) {
    return 'nunca';
  }
  const at = new Date(value);
  if (now.getTime() - at.getTime() < 60_000) {
    return 'agora';
  }
  const today = toIsoDate(now);
  const day = toIsoDate(at);
  if (day === today) {
    return formatTime(at);
  }
  return day === addDaysToIsoDate(today, -1) ? `ontem ${formatTime(at)}` : formatShortDate(at);
}

export interface DiffRow {
  k: string;
  before: string;
  after: string;
  changed: boolean;
}

function render(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * `before`/`after` lado a lado (foco do drawer). A união das chaves dos dois lados:
 * campo que só existe depois é criação, campo que só existe antes é remoção, os dois
 * precisam aparecer, senão o diff esconde justamente a mudança. `-` no lado ausente é a
 * copy do croqui.
 */
export function diffRows(entry: Pick<AuditEntry, 'before' | 'after'>): DiffRow[] {
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const fields = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return fields.map((k) => {
    const left = render(before[k]);
    const right = render(after[k]);
    return { k, before: left ?? '-', after: right ?? '-', changed: left !== right };
  });
}

const BROWSERS: readonly [RegExp, string][] = [
  [/Edg\//, 'Edge'],
  [/OPR\/|Opera/, 'Opera'],
  [/Chrome\//, 'Chrome'],
  [/Firefox\//, 'Firefox'],
  [/Safari\//, 'Safari'],
];

const SYSTEMS: readonly [RegExp, string][] = [
  [/Windows/, 'Windows'],
  [/iPhone|iPad/, 'iOS'],
  [/Mac OS X/, 'macOS'],
  [/Android/, 'Android'],
  [/Linux/, 'Linux'],
];

function firstMatch(value: string, table: readonly [RegExp, string][]): string | undefined {
  return table.find(([pattern]) => pattern.test(value))?.[1];
}

/**
 * `Chrome, macOS` a partir do `user_agent` (inventário: parse client-side). Só navegador
 * e sistema: a versão não muda decisão nenhuma e o croqui não a mostra. `undefined`
 * quando o UA não diz, um "desconhecido, desconhecido" seria pior que a ausência.
 */
export function parseUserAgent(userAgent: string | null): string | undefined {
  if (userAgent === null || userAgent.trim() === '') {
    return undefined;
  }
  const browser = firstMatch(userAgent, BROWSERS);
  const system = firstMatch(userAgent, SYSTEMS);
  const parts = [browser, system].filter((part): part is string => part !== undefined);
  return parts.length === 0 ? undefined : parts.join(', ');
}

/**
 * O `StepUpDialog` cancelado devolve o `403 mfa_required` original (`api/client.ts`):
 * é desistência do operador, não falha, a tela não pinta erro vermelho por isso.
 */
export function isStepUpCancelled(error: unknown): boolean {
  return isApiError(error) && error.code === 'mfa_required';
}
